import { useState } from 'react';
import { Stack, Paper, TextInput, Group, Text, Skeleton, Badge, SimpleGrid, Card, ThemeIcon, Collapse, Button } from '@mantine/core';
import { IconSearch, IconBulb, IconChevronDown, IconChevronUp, IconCurrencyEuro, IconClick, IconTarget, IconEye } from '@tabler/icons-react';
import { useGoogleAdsRecommendations, useGoogleAdsRecommendationImpact, useGoogleAdsAccount } from '@/hooks/useGoogleAds';
import { microsToAmount } from '@/types/googleAds';
import type { Recommendation } from '@/types/googleAds';
import { MetricCard } from '../components/MetricCard';

interface RecommendationsTabProps {
  customerId: string;
}

const RECOMMENDATION_TYPE_LABELS: Record<string, string> = {
  CAMPAIGN_BUDGET: 'Kampagnenbudget',
  KEYWORD: 'Keyword',
  TEXT_AD: 'Textanzeige',
  TARGET_CPA_OPT_IN: 'Ziel-CPA aktivieren',
  MAXIMIZE_CONVERSIONS_OPT_IN: 'Conversions maximieren',
  ENHANCED_CPC_OPT_IN: 'Enhanced CPC',
  SEARCH_PARTNERS_OPT_IN: 'Suchnetzwerk-Partner',
  MAXIMIZE_CLICKS_OPT_IN: 'Klicks maximieren',
  OPTIMIZE_AD_ROTATION: 'Anzeigenrotation optimieren',
  KEYWORD_MATCH_TYPE: 'Keyword Match Type',
  MOVE_UNUSED_BUDGET: 'Ungenutztes Budget verschieben',
  FORECASTING_CAMPAIGN_BUDGET: 'Budgetprognose',
  TARGET_ROAS_OPT_IN: 'Ziel-ROAS aktivieren',
  RESPONSIVE_SEARCH_AD: 'Responsive Suchanzeige',
  MARGINAL_ROI_CAMPAIGN_BUDGET: 'ROI-optimiertes Budget',
  USE_BROAD_MATCH_KEYWORD: 'Broad Match verwenden',
  RESPONSIVE_SEARCH_AD_ASSET: 'RSA Asset',
  UPGRADE_SMART_SHOPPING_CAMPAIGN_TO_PERFORMANCE_MAX: 'Zu Performance Max upgraden',
  RESPONSIVE_SEARCH_AD_IMPROVE_AD_STRENGTH: 'Anzeigenstärke verbessern',
  DISPLAY_EXPANSION_OPT_IN: 'Display-Erweiterung',
  UPGRADE_LOCAL_CAMPAIGN_TO_PERFORMANCE_MAX: 'Lokale zu PMax upgraden',
  RAISE_TARGET_CPA_BID_TOO_LOW: 'Ziel-CPA erhöhen',
  FORECASTING_SET_TARGET_ROAS: 'ROAS-Ziel setzen',
  CALLOUT_ASSET: 'Callout-Asset',
  SITELINK_ASSET: 'Sitelink-Asset',
  CALL_ASSET: 'Anruf-Asset',
  SHOPPING_MIGRATE_REGULAR_SHOPPING_CAMPAIGN_OFFERS_TO_PERFORMANCE_MAX: 'Shopping zu PMax migrieren',
  SHOPPING_ADD_AGE_GROUP: 'Altersgruppe hinzufügen',
  SHOPPING_ADD_COLOR: 'Farbe hinzufügen',
  SHOPPING_ADD_GENDER: 'Geschlecht hinzufügen',
  SHOPPING_ADD_SIZE: 'Größe hinzufügen',
  SHOPPING_ADD_GTIN: 'GTIN hinzufügen',
  SHOPPING_ADD_MORE_IDENTIFIERS: 'Identifikatoren hinzufügen',
  SHOPPING_ADD_PRODUCTS_TO_CAMPAIGN: 'Produkte hinzufügen',
  SHOPPING_FIX_DISAPPROVED_PRODUCTS: 'Abgelehnte Produkte korrigieren',
  SHOPPING_TARGET_ALL_OFFERS: 'Alle Angebote targeten',
  SHOPPING_FIX_SUSPENDED_MERCHANT_CENTER_ACCOUNT: 'Merchant Center entsperren',
  SHOPPING_FIX_MERCHANT_CENTER_ACCOUNT_SUSPENSION_WARNING: 'MC Sperrwarnung',
};

function getRecommendationTypeLabel(type: string): string {
  return RECOMMENDATION_TYPE_LABELS[type] ?? type.replace(/_/g, ' ');
}

function getRecommendationTypeColor(type: string): string {
  if (type.includes('BUDGET')) return 'blue';
  if (type.includes('KEYWORD')) return 'green';
  if (type.includes('AD')) return 'violet';
  if (type.includes('SHOPPING')) return 'orange';
  if (type.includes('ASSET')) return 'cyan';
  if (type.includes('OPT_IN') || type.includes('UPGRADE')) return 'teal';
  return 'gray';
}

export function RecommendationsTab({ customerId }: RecommendationsTabProps) {
  const { data: recommendations, isLoading: recsLoading } = useGoogleAdsRecommendations(customerId);
  const { data: impacts, isLoading: impactsLoading } = useGoogleAdsRecommendationImpact(customerId);
  const { data: account } = useGoogleAdsAccount(customerId);

  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const formatCurrency = (micros: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: account?.currency ?? 'EUR' }).format(
      microsToAmount(micros),
    );

  const formatNumber = (num: number) => new Intl.NumberFormat('de-DE').format(Math.round(num));

  const isLoading = recsLoading || impactsLoading;

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const filteredRecommendations = recommendations?.filter((rec) => {
    if (rec.dismissed) return false;
    if (search) {
      const searchLower = search.toLowerCase();
      const typeLabel = getRecommendationTypeLabel(rec.type).toLowerCase();
      if (!typeLabel.includes(searchLower) && !rec.campaignName?.toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    return true;
  });

  const impactMap = new Map(impacts?.map((i) => [i.recommendationId, i]));

  const groupedRecommendations = filteredRecommendations?.reduce(
    (acc, rec) => {
      const type = rec.type;
      if (!acc[type]) acc[type] = [];
      acc[type].push(rec);
      return acc;
    },
    {} as Record<string, Recommendation[]>,
  );

  const totalPotentialClicks = impacts?.reduce((sum, i) => sum + (i.impactMetrics.potentialClicks ?? 0), 0) ?? 0;
  const totalPotentialConversions = impacts?.reduce((sum, i) => sum + (i.impactMetrics.potentialConversions ?? 0), 0) ?? 0;
  const totalPotentialImpressions = impacts?.reduce((sum, i) => sum + (i.impactMetrics.potentialImpressions ?? 0), 0) ?? 0;

  if (isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={100} />
        <Skeleton height={400} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 2, md: 4 }}>
        <MetricCard
          title="Empfehlungen"
          value={filteredRecommendations?.length ?? 0}
          icon={<IconBulb size={16} />}
          color="yellow"
        />
        <MetricCard
          title="Potenzielle Impressionen"
          value={formatNumber(totalPotentialImpressions)}
          icon={<IconEye size={16} />}
          color="cyan"
        />
        <MetricCard
          title="Potenzielle Klicks"
          value={formatNumber(totalPotentialClicks)}
          icon={<IconClick size={16} />}
          color="blue"
        />
        <MetricCard
          title="Potenzielle Conversions"
          value={formatNumber(totalPotentialConversions)}
          icon={<IconTarget size={16} />}
          color="violet"
        />
      </SimpleGrid>

      <Paper withBorder p="md">
        <Group>
          <TextInput
            placeholder="Empfehlung suchen..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={300}
          />
          <Text size="sm" c="dimmed" ml="auto">
            {Object.keys(groupedRecommendations ?? {}).length} Kategorien
          </Text>
        </Group>
      </Paper>

      {groupedRecommendations && Object.keys(groupedRecommendations).length > 0 ? (
        <Stack gap="md">
          {Object.entries(groupedRecommendations)
            .sort(([, a], [, b]) => b.length - a.length)
            .map(([type, recs]) => (
              <Paper key={type} withBorder>
                <Group
                  p="md"
                  justify="space-between"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleExpand(type)}
                >
                  <Group>
                    <ThemeIcon variant="light" color={getRecommendationTypeColor(type)} size="lg">
                      <IconBulb size={18} />
                    </ThemeIcon>
                    <div>
                      <Text fw={500}>{getRecommendationTypeLabel(type)}</Text>
                      <Text size="xs" c="dimmed">
                        {recs.length} Empfehlung{recs.length !== 1 ? 'en' : ''}
                      </Text>
                    </div>
                  </Group>
                  <Group>
                    <Badge color={getRecommendationTypeColor(type)} variant="light" size="lg">
                      {recs.length}
                    </Badge>
                    {expandedIds.has(type) ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
                  </Group>
                </Group>

                <Collapse in={expandedIds.has(type)}>
                  <Stack gap="sm" p="md" pt={0}>
                    {recs.map((rec) => {
                      const impact = impactMap.get(rec.id);
                      return (
                        <Card key={rec.id} withBorder padding="sm">
                          <Group justify="space-between" wrap="nowrap">
                            <div style={{ flex: 1 }}>
                              <Text size="sm" fw={500}>
                                {rec.campaignName ?? 'Kontoebene'}
                              </Text>
                              {rec.adGroupName && (
                                <Text size="xs" c="dimmed">
                                  {rec.adGroupName}
                                </Text>
                              )}
                            </div>
                            {impact && (
                              <Group gap="lg">
                                {impact.impactMetrics.potentialImpressions && (
                                  <div style={{ textAlign: 'right' }}>
                                    <Text size="xs" c="dimmed">
                                      Impressionen
                                    </Text>
                                    <Text size="sm" fw={500} c="cyan">
                                      +{formatNumber(impact.impactMetrics.potentialImpressions)}
                                    </Text>
                                  </div>
                                )}
                                {impact.impactMetrics.potentialClicks && (
                                  <div style={{ textAlign: 'right' }}>
                                    <Text size="xs" c="dimmed">
                                      Klicks
                                    </Text>
                                    <Text size="sm" fw={500} c="blue">
                                      +{formatNumber(impact.impactMetrics.potentialClicks)}
                                    </Text>
                                  </div>
                                )}
                                {impact.impactMetrics.potentialConversions && (
                                  <div style={{ textAlign: 'right' }}>
                                    <Text size="xs" c="dimmed">
                                      Conversions
                                    </Text>
                                    <Text size="sm" fw={500} c="violet">
                                      +{formatNumber(impact.impactMetrics.potentialConversions)}
                                    </Text>
                                  </div>
                                )}
                                {impact.impactMetrics.potentialCostMicros && (
                                  <div style={{ textAlign: 'right' }}>
                                    <Text size="xs" c="dimmed">
                                      Kosten
                                    </Text>
                                    <Text size="sm" fw={500} c="green">
                                      {formatCurrency(impact.impactMetrics.potentialCostMicros)}
                                    </Text>
                                  </div>
                                )}
                              </Group>
                            )}
                          </Group>
                        </Card>
                      );
                    })}
                  </Stack>
                </Collapse>
              </Paper>
            ))}
        </Stack>
      ) : (
        <Paper withBorder p="xl">
          <Stack align="center" gap="md">
            <ThemeIcon size={60} variant="light" color="green">
              <IconBulb size={32} />
            </ThemeIcon>
            <Text size="lg" fw={500}>
              Keine offenen Empfehlungen
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Ihr Google Ads Konto ist optimal eingerichtet. Es gibt derzeit keine Empfehlungen zur
              Verbesserung.
            </Text>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
